import { cargarCuotasPrestamo } from './prestamoCuotas'
import TablaPrestamoRecibido from './TablaPrestamoRecibido'
import CustomButton from '../../../../../../ui/CustomButton'
import { useContext, useEffect, useRef, useState } from 'react'
import { BusinessAPI } from '../../../../../../../../api/business.api'
import GlobalContext from '../../../../../../../../context/global-context'
import { Title } from '../../../../../../ui'
import ResizableModal from '../../../../../../ui/ResizableModal'
import { OrganizationAPI } from '../../../../../../../../api/organization.api'
import { sileo } from 'sileo'

const InputField = ({ label, placeholder, type, value, onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm
        ${
          disabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'focus:border-indigo-500 focus:ring-indigo-500'
        }
      `}
    />
  </div>
)

const TextAreaField = ({ label, value, onChange, ...props }) => (
  <div>
    <label className='block text-sm font-medium text-gray-700'>{label}</label>
    <textarea
      className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
      value={value}
      onChange={onChange}
      maxLength={50}
      required
      {...props}
    />
    <p className='text-xs text-gray-500 mt-1'>{value.length}/50 caracteres</p>
  </div>
)

const PrestamoRecibido = ({
  open,
  setOpen,
  type = 'ingreso',
  idLibro,
  fVenc,
  folio,
  setSwitchDate,
  action
}) => {
  const [fecha, setFecha] = useState('')
  const [glosa, setGlosa] = useState('')
  const [rut, setRut] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [archivoAdjunto, setArchivoAdjunto] = useState(null)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const fileInputRef = useRef(null)
  const isCreateMode = action === 'crear' || !idLibro
  const [modoCarga, setModoCarga] = useState('masiva')
  const [isSaving, setIsSaving] = useState(false)
  const isMassiveMode = isCreateMode && modoCarga === 'masiva'
  const businessAPI = new BusinessAPI()
  const { ui, currentBusiness } = useContext(GlobalContext)
  const organizationAPI = new OrganizationAPI()
  const [data, setData] = useState({})
  const [, setLoading] = useState(false)
  const [monto, setMonto] = useState('') 
  const [capital, setCapital] = useState('') // Capital
  const [intereses, setIntereses] = useState('') // Intereses
  const [costoTotal, setCostoTotal] = useState('') // Costo Total
  const [filasPrestamo, setFilasPrestamo] = useState(
    Array.from({ length: 1 }, () => ({
      fecha: '',
      glosa: '',
      otrosCargos: '',
      capital: '',
      interes: '',
      total: ''
    }))
  )

  const handleClose = () => setOpen(false)

  const handleDownloadTemplate = async () => {
    if (isDownloadingTemplate) return
    setIsDownloadingTemplate(true)
    try {
      const response = await businessAPI.downloadRemunerationTemplate('plantillas-prestamo')
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      try {
        link.href = url
        link.download = 'PrestamoRecibido.xlsx'
        document.body.appendChild(link)
        link.click()
      } finally {
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      sileo.error({
        title: 'Error de Descarga',
        description: 'No se pudo descargar la plantilla. Intenta nuevamente.'
      })
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const functionGetData = async () => {
    try {
      setLoading(true)
      const response = await organizationAPI.getDataLibroContable(
        currentBusiness?.id,
        idLibro,
        type.trim()
      )
      setData(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && idLibro) {
      functionGetData()
    }
  }, [open, idLibro])

  useEffect(() => {
    if (data) {
      if (data.fecha) {
        const parsedDate = new Date(data.fecha)
        const formattedDate = parsedDate.toISOString().split('T')[0]
        setFecha(formattedDate)
      }
      if (data.fechaVencimiento) {
        const parsedDate = new Date(data.fecha)
        const formattedDate = parsedDate.toISOString().split('T')[0]
        setFecha(formattedDate)
      }
      if (data.glosa) setGlosa(data.glosa)
      if (data.montoTotal) setMonto(data.montoTotal.toString())
      if (data.razonSocial) setRazonSocial(data.razonSocial)
      if (data.rut) setRut(data.rut)
      // Aquí mapeamos prestamoCuotas
      if (Array.isArray(data.prestamoCuotas) || Array.isArray(data.linkage)) {
        setFilasPrestamo(cargarCuotasPrestamo(data))
      }
    }
  }, [data])

  const handleDatos = async () => {
    if (!archivoAdjunto) {
      sileo.error({
        title: 'Campos Obligatorios',
        description: 'Debes adjuntar un archivo Excel.'
      })
      return
    }

    const payload = new FormData()
    payload.append('file', archivoAdjunto)
    try {
      ui.setLoader({ visible: true })
      await businessAPI.postPagoArriendo(
        currentBusiness.id,
        payload,
        'prestamo-recibido'
      )
      setSwitchDate(true)
      handleClose()
    } catch (error) {
      sileo.error({
        title: 'Error al cargar préstamo',
        description: 'No se pudo cargar el archivo. Intenta nuevamente.'
      })
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  const handleCreateIndividual = async () => {
    if (!fecha || !razonSocial.trim() || !rut || !glosa.trim() || !filasPrestamo.length ||
      filasPrestamo.some(fila => !fila.fecha || Number(fila.capital) <= 0 || !Number.isFinite(Number(fila.capital)) || ['interes', 'otrosCargos'].some(field => !Number.isFinite(Number(fila[field] || 0)) || Number(fila[field] || 0) < 0))) {
      sileo.error({ title: 'Campos obligatorios', description: 'Completa fecha, contraparte, RUT, detalle y las cuotas con fecha y capital mayor a cero.' })
      return
    }
    const payload = {
      fecha,
      vencimiento: null,
      razonSocial: razonSocial.trim(),
      rut,
      rutId: null,
      cuentaContableId: 346,
      nombreComprobante: null,
      nombreComprobanteId: null,
      glosa: glosa.trim(),
      respaldoId: null,

      monto: Number(monto),

      prestamoCuotas: filasPrestamo.map(fila => ({
        fecha: fila.fecha,
        capital: Number(fila.capital),
        interes: Number(fila.interes || 0),
        otrosCargos: Number(fila.otrosCargos || 0),
        glosa: fila.glosa?.trim() || undefined
      }))
    }
    try {
      ui.setLoader({ visible: true })
      await businessAPI.postPagoArriendo(currentBusiness.id, payload, 'prestamo-recibido')
      setSwitchDate(true)
      handleClose()
    } catch (error) {
      sileo.error({ title: 'Error al guardar', description: 'No se pudo crear el préstamo. Intenta nuevamente.' })
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  const handleSave = async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      await (isCreateMode ? isMassiveMode ? handleDatos() : handleCreateIndividual() : handleSubmit())
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!filasPrestamo.length || filasPrestamo.some(fila => !fila.fecha || Number(fila.capital) <= 0 || !Number.isFinite(Number(fila.capital)) || ['interes', 'otrosCargos'].some(field => !Number.isFinite(Number(fila[field] || 0)) || Number(fila[field] || 0) < 0))) {
      sileo.error({ title: 'Cuotas incompletas', description: 'Completa la fecha y el desglose de cada cuota, con capital mayor a cero.' })
      return
    }
    try {
      // Mapeamos las filas a formato de la API
      const cuotas = filasPrestamo.map(fila => ({
        fecha: fila.fecha,
        capital: Number(fila.capital || 0),
        interes: Number(fila.interes || 0),
        otrosCargos: Number(fila.otrosCargos || 0),
        glosa: fila.glosa?.trim() || undefined
      }))

      const payload = {
        fecha: fecha,
        glosa: glosa,
        monto: Number(monto),
        razonSocial: razonSocial,
        validation: 1,
        vencimiento: fVenc,
        prestamoCuotas: cuotas // <-- lo agregas aquí
      }
      await organizationAPI.updateVinculacion(
        currentBusiness?.id,
        idLibro,
        payload
      )
      setSwitchDate(true)
      functionGetData()
      handleClose()
    } catch (error) {
      console.error(error)
    } finally {
      //ui.setLoader({ visible: false})
    }
  }

  const formatMonto = value => {
    if (!value) return ''
    const numericValue = value.replace(/\D/g, '')
    if (!numericValue) return ''
    return new Intl.NumberFormat('es-CL').format(numericValue)
  }

  const formatRut = input => {
    let cleaned = input.replace(/[^0-9kK]/g, '').toUpperCase()
    // Limita a 9 caracteres (8 cuerpo + 1 DV)
    if (cleaned.length > 9) cleaned = cleaned.slice(0, 9)
    const body = cleaned.slice(0, -1)
    const dv = cleaned.slice(-1)
    let formattedBody = ''
    for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
      formattedBody = body[i] + formattedBody
      if (j % 3 === 0 && i !== 0) {
        formattedBody = '.' + formattedBody
      }
    }
    if (body.length === 0) return dv
    return `${formattedBody}-${dv}`
  }

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    setFecha(today)
  }, [])

  useEffect(() => {
    if (!open) {
      setModoCarga('masiva')
      setArchivoAdjunto(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [open])

  useEffect(() => {
    const capitalTotal = filasPrestamo.reduce( (acc, fila) => acc + Number(fila.capital || 0),  0)
    const interesesTotal = filasPrestamo.reduce( (acc, fila) => acc + Number(fila.interes || 0), 0 )
    const costoTotalSuma = filasPrestamo.reduce( (acc, fila) => acc + Number(fila.total || 0), 0 )

    setCapital(String(capitalTotal))
    setIntereses(String(interesesTotal))
    setCostoTotal(String(costoTotalSuma))
  }, [filasPrestamo])

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton
            title='CANCELAR'
            color='primary'
            className='rounded-full'
            onClick={handleClose}
          />
          <CustomButton
            title='GRABAR VINCULACIÓN'
            color='accent2'
            className='rounded-full'
            onClick={handleSave}
            disabled={isSaving}
          />
        </>
      }
    >
      <div className='mt-4'>
        <div className='flex justify-between items-center mb-6'>
          <div>
            <Title className='text-3xl'>Credito Consumo</Title>
          </div>
        </div>

        <div className='bg-white h-full p-6 rounded-lg shadow-md'>
          {isCreateMode && (
            <div className='mb-6 ml-auto flex w-fit rounded-full border border-gray-200 bg-gray-100 p-1 shadow-sm'>
              {[['individual', 'Carga individual'], ['masiva', 'Carga masiva']].map(([mode, label]) => (
                <button key={mode} type='button' disabled={isSaving} onClick={() => setModoCarga(mode)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold uppercase transition-colors ${modoCarga === mode ? 'bg-accent2 text-white shadow-sm' : 'text-gray-500 hover:text-primary'}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {isMassiveMode ? (
            <div className='grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]'>
              <div className='rounded-2xl border border-slate-200 bg-slate-50 p-5'>
                <h3 className='text-xl font-semibold text-gray-900'>Carga mediante archivo Excel</h3>
                <p className='mt-3 text-sm font-semibold text-gray-800'>
                  Para ingresar los datos del préstamo, sigue estas instrucciones:
                </p>
                <ol className='mt-3 list-decimal space-y-3 pl-5 text-sm text-gray-600'>
                  <li>
                    <CustomButton
                      type='button'
                      title={isDownloadingTemplate ? 'DESCARGANDO...' : 'DESCARGAR PLANTILLA EXCEL'}
                      color='accent2'
                      className='rounded-full text-xs'
                      disabled={isDownloadingTemplate}
                      onClick={handleDownloadTemplate}
                    />
                  </li>
                  <li>Completa los datos solicitados en la plantilla.</li>
                  <li>Guarda el archivo en formato Excel (.xlsx o .xls).</li>
                  <li>Selecciona el archivo completado y presiona Grabar vinculación.</li>
                </ol>

              </div>

              <div className='rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6'>
                <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
                  <div>
                    <p className='text-sm font-semibold text-gray-800'>Archivo adjunto</p>
                    <p className='mt-1 text-xs text-gray-500'>Selecciona el respaldo del préstamo.</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.xlsx,.xls'
                    className='hidden'
                    onChange={e => setArchivoAdjunto(e.target.files?.[0] || null)}
                  />
                  {archivoAdjunto ? (
                    <div className='w-full rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-left'>
                      <p className='text-xs font-semibold text-primary'>Archivo seleccionado</p>
                      <p className='mt-1 break-all text-sm text-gray-700'>{archivoAdjunto.name}</p>
                      <button
                        type='button'
                        onClick={() => {
                          setArchivoAdjunto(null)
                          if (fileInputRef.current) fileInputRef.current.value = ''
                        }}
                        className='mt-3 text-xs font-semibold text-red-500 hover:text-red-600'
                      >
                        Quitar archivo
                      </button>
                    </div>
                  ) : (
                    <p className='w-full rounded-2xl bg-white/80 px-4 py-6 text-sm text-gray-500'>
                      Aún no has seleccionado un archivo.
                    </p>
                  )}
                  <CustomButton
                    type='button'
                    title={archivoAdjunto ? 'CAMBIAR ARCHIVO' : 'SELECCIONAR ARCHIVO'}
                    color='primary'
                    className='w-full rounded-full'
                    onClick={() => fileInputRef.current?.click()}
                  />
                </div>
              </div>
            </div>
          ) : (
          <div className='flex w-full flex-col gap-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 w-full'>
              <InputField label='Folio' value={folio} type='text' disabled />
              <div>
                <label className='block text-sm font-medium text-gray-700'>Fecha</label>
                <input
                  type='date'
                  value={fecha}
                  onChange={e => setFecha(e.target.value)}
                  className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                />
              </div>
              <InputField
                label='Contraparte'
                placeholder='Nombre'
                type='text'
                value={razonSocial}
                onChange={e => setRazonSocial(e.target.value)}
              />
              <InputField
                label='RUT'
                placeholder='11.111.111-1'
                type='text'
                value={rut}
                onChange={e => setRut(formatRut(e.target.value))}
              />
              <InputField
                label='Clasificación'
                placeholder='2-1-11-1 Otros Acreedores'
                type='text'
                disabled
              />
              <InputField
                label='Monto Liquido Otorgado'
                placeholder='$'
                type='text'
                value={formatMonto(monto)}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
              />
              <InputField
                label='Capital'
                placeholder='$'
                type='text'
                value={formatMonto(capital)}
                disabled
              />
              <InputField
                label='Intereses Por Devengar'
                placeholder='$'
                type='text'
                value={formatMonto(intereses)}
                disabled
              />
              <InputField
                label='Costo Total credito'
                placeholder='$'
                type='text'
                value={formatMonto(costoTotal)}
                disabled
              />
              <TextAreaField
                label='Detalle'
                placeholder='Describe en detalle la naturaleza, origen o lo que mejor describa la vinculación que vamos a crear.'
                value={glosa}
                onChange={e => setGlosa(e.target.value)}
              />
            </div>
              <div className='w-full'>
                <h3 className='mb-3 text-sm font-semibold text-gray-800'>Cuotas del préstamo</h3>
                <TablaPrestamoRecibido filasPrestamo={filasPrestamo} setFilasPrestamo={setFilasPrestamo} capitalReadOnly={!isCreateMode} />
              </div>
          </div>
          )}
        </div>
      </div>
    </ResizableModal>
  )
}

export default PrestamoRecibido
